import React, { useState, useMemo } from "react";
import { Folder, Clock, FileText, X, Check, Info } from "lucide-react";
import { INK, INK_2, INK_3, LINE, BURGUNDY, BURGUNDY_TINT, BAD_INK } from "../../Bureau/bureauTokens";
import { useEtatGlobal } from "../../EtatGlobal.js";

const DUREES = ["30", "45", "60", "90", "120", "180"];

function pad(n) {
  return String(n).padStart(2, "0");
}
function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function isoOfDate(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function toMin(t) {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

const fieldLabel = "text-[11px] font-medium";
const fieldInput = "h-9 px-3 text-xs outline-none";
const fieldStyle = { border: `1px solid ${LINE}`, borderRadius: 8, color: INK, background: "#fff" };

// Fidèle à MyALFA Worksync Test V2/planning.jsx (NewRoomBookingModal) :
// salle en boutons-puces colorés, créneau + durée + réservé par, titre, et
// message de conflit calculé en direct contre les réservations déjà
// chargées pour la semaine affichée (`roomBookings`) — le rejet 409 côté
// serveur (salleController.createReservationSalle) reste le dernier
// recours, notamment si la date choisie sort de la semaine affichée.
function NewRoomBookingModal({ prefill, rooms, roomBookings, onCreate, onClose }) {
  const { users, utilisateur } = useEtatGlobal();
  const [salleId, setSalleId] = useState(rooms[0]?.id ?? null);
  const [date, setDate] = useState(prefill?.date || todayIso());
  const [time, setTime] = useState(prefill?.time || "09:00");
  const [duree, setDuree] = useState("60");
  const [userId, setUserId] = useState(utilisateur?.id ?? "");
  const [titre, setTitre] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const conflict = useMemo(() => {
    if (!salleId) return null;
    const start = toMin(time);
    const end = start + parseInt(duree, 10);
    return (roomBookings || []).find((r) => {
      if (r.salleId !== salleId) return false;
      if (isoOfDate(new Date(r.date)) !== date) return false;
      const rStart = toMin(r.heure);
      return start < rStart + r.duree && end > rStart;
    });
  }, [salleId, date, time, duree, roomBookings]);

  const canSave = !!salleId && !!date && !!time && !!userId && !!titre.trim() && !conflict && !saving;

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      await onCreate({ salleId, date, heure: time, duree: parseInt(duree, 10), titre: titre.trim(), userId });
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de la création de la réservation.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[210] flex items-center justify-center p-4" style={{ background: "rgba(31,22,32,.45)" }} onClick={onClose}>
      <div
        className="w-full flex flex-col overflow-hidden"
        style={{ maxWidth: 600, maxHeight: "88vh", background: "#fff", borderRadius: 18, boxShadow: "0 24px 60px rgba(20,14,24,.30)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3.5 px-5 py-4 flex-shrink-0" style={{ borderBottom: `1px solid ${LINE}` }}>
          <div
            className="flex items-center justify-center flex-shrink-0"
            style={{ width: 40, height: 40, borderRadius: 10, background: BURGUNDY_TINT, color: BURGUNDY }}
          >
            <Folder size={19} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold m-0" style={{ color: INK }}>
              Réserver une salle
            </h2>
            <div className="text-[11px] mt-0.5" style={{ color: INK_3 }}>
              Bloquer une salle de réunion sur un créneau
            </div>
          </div>
          <button onClick={onClose} className="flex items-center justify-center flex-shrink-0" style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${LINE}` }}>
            <X size={14} style={{ color: INK_2 }} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {error && (
            <p className="text-xs mb-2" style={{ color: BAD_INK }}>
              {error}
            </p>
          )}

          {/* Salle */}
          <div className="mb-3" style={{ background: "#FFF6F8", border: "1px solid #E8C8D2", borderRadius: 10, padding: "12px 14px" }}>
            <div className="flex items-center gap-1.5 text-xs font-semibold mb-2" style={{ color: BURGUNDY }}>
              <Folder size={13} /> Salle
            </div>
            {rooms.length === 0 ? (
              <p className="text-xs" style={{ color: INK_3 }}>
                Aucune salle enregistrée — ajoutez-en une via "Gérer les salles".
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {rooms.map((room) => (
                  <button
                    key={room.id}
                    onClick={() => setSalleId(room.id)}
                    className="text-xs font-semibold px-3 py-1.5"
                    style={{
                      borderRadius: 999,
                      border: `1px solid ${salleId === room.id ? room.couleur : LINE}`,
                      background: salleId === room.id ? room.couleur : "#fff",
                      color: salleId === room.id ? "#fff" : INK_2,
                    }}
                  >
                    {room.nom} · {room.capacite} pl.
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Créneau */}
          <div className="mb-3" style={{ background: "#FAFAF8", border: `1px solid ${LINE}`, borderRadius: 10, padding: "12px 14px" }}>
            <div className="flex items-center gap-1.5 text-xs font-semibold mb-2" style={{ color: BURGUNDY }}>
              <Clock size={13} /> Créneau
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1">
                <span className={fieldLabel} style={{ color: INK_2 }}>
                  Date <span style={{ color: BAD_INK }}>*</span>
                </span>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={fieldInput} style={fieldStyle} />
              </label>
              <label className="flex flex-col gap-1">
                <span className={fieldLabel} style={{ color: INK_2 }}>
                  Heure <span style={{ color: BAD_INK }}>*</span>
                </span>
                <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className={fieldInput} style={fieldStyle} />
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-2.5">
              <label className="flex flex-col gap-1">
                <span className={fieldLabel} style={{ color: INK_2 }}>
                  Durée (min)
                </span>
                <select value={duree} onChange={(e) => setDuree(e.target.value)} className={`${fieldInput} appearance-none cursor-pointer`} style={fieldStyle}>
                  {DUREES.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className={fieldLabel} style={{ color: INK_2 }}>
                  Réservé par
                </span>
                <select
                  value={userId}
                  onChange={(e) => setUserId(Number(e.target.value))}
                  className={`${fieldInput} appearance-none cursor-pointer`}
                  style={fieldStyle}
                >
                  {(users || []).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} {u.lastName}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {conflict && (
              <div className="flex items-center gap-1.5 mt-2.5 text-[11px]" style={{ color: BAD_INK }}>
                <Info size={12} /> Salle déjà réservée sur ce créneau ({conflict.heure} · {conflict.titre}).
              </div>
            )}
          </div>

          {/* Objet */}
          <div style={{ background: "#FAFAF8", border: `1px solid ${LINE}`, borderRadius: 10, padding: "12px 14px" }}>
            <div className="flex items-center gap-1.5 text-xs font-semibold mb-2" style={{ color: BURGUNDY }}>
              <FileText size={13} /> Objet
            </div>
            <label className="flex flex-col gap-1">
              <span className={fieldLabel} style={{ color: INK_2 }}>
                Intitulé <span style={{ color: BAD_INK }}>*</span>
              </span>
              <input
                value={titre}
                onChange={(e) => setTitre(e.target.value)}
                placeholder="ex. Réunion associés, RDV client…"
                className={fieldInput}
                style={fieldStyle}
              />
            </label>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3.5 flex-shrink-0" style={{ borderTop: `1px solid ${LINE}`, background: "#FAFAF8" }}>
          <button onClick={onClose} className="px-4 py-2 text-xs" style={{ border: `1px solid ${LINE}`, borderRadius: 10, color: INK_2, background: "#fff" }}>
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={!canSave}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white disabled:opacity-50"
            style={{ background: BURGUNDY, borderRadius: 10 }}
          >
            <Check size={13} /> {saving ? "Réservation…" : "Réserver"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default NewRoomBookingModal;
